/**
 * Shared import card matcher hook
 *
 * Generalizes the card matching logic to work with any card-like type
 * that has name, setCode, collectorNumber, and optionally scryfallId.
 *
 * Used by collection imports (ImportCard), deck imports (DeckImportCard),
 * and list imports (ParsedListCard).
 */
import {
  cardIndexByIdAtom,
  cardIndexByNameAtom,
  cardIndexBySetNumberAtom,
  sortedCardIndexNamesAtom,
  type MinimalCard,
} from '@/services/cardDatabase';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

/** Minimal shape required for card matching — all import card types satisfy this */
export interface MatchableCard {
  name: string;
  setCode?: string | null;
  collectorNumber?: string | null;
  scryfallId?: string | null;
}

export type MatchType = 'exact' | 'name' | 'fuzzy' | 'none';

export interface CardMatchResult {
  matchedCard: MinimalCard | null;
  matchType: MatchType;
  matchError?: string;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook providing card matching functions against the preloaded card index.
 *
 * Matching priority:
 * 1. Scryfall ID (exact)
 * 2. Set + collector number (exact)
 * 3. Exact name match
 * 4. Fuzzy prefix search
 */
export function useImportCardMatcher() {
  const cardsById = useAtomValue(cardIndexByIdAtom);
  const cardsBySetNumber = useAtomValue(cardIndexBySetNumberAtom);
  const cardsByName = useAtomValue(cardIndexByNameAtom);
  const sortedNames = useAtomValue(sortedCardIndexNamesAtom);

  const getCardById = useCallback((id: string): MinimalCard | null => cardsById.get(id) ?? null, [cardsById]);

  const getCardBySetNumber = useCallback(
    (setCode: string, collectorNumber: string): MinimalCard | null => {
      const key = `${setCode.toLowerCase()}:${collectorNumber}`;
      return cardsBySetNumber.get(key) ?? null;
    },
    [cardsBySetNumber],
  );

  const getCardByName = useCallback((name: string): MinimalCard | null => cardsByName.get(name.toLowerCase()) ?? null, [cardsByName]);

  const searchCardsByPrefix = useCallback(
    (prefix: string, maxResults: number = 10): MinimalCard[] => {
      const lowerPrefix = prefix.toLowerCase();
      const results: MinimalCard[] = [];

      let left = 0;
      let right = sortedNames.length - 1;
      let firstMatch = -1;

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const name = sortedNames[mid].name;
        if (name.startsWith(lowerPrefix)) {
          firstMatch = mid;
          right = mid - 1;
        } else if (name < lowerPrefix) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      if (firstMatch === -1) return results;

      for (let i = firstMatch; i < sortedNames.length && results.length < maxResults; i++) {
        const item = sortedNames[i];
        if (item.name.startsWith(lowerPrefix)) {
          results.push(item.card);
        } else {
          break;
        }
      }

      return results;
    },
    [sortedNames],
  );

  /** Match a single card against the index */
  const matchCard = useCallback(
    (card: MatchableCard): CardMatchResult => {
      let matchedCard: MinimalCard | null = null;
      let matchType: MatchType = 'none';

      // Priority 1: Try direct Scryfall ID match
      if (card.scryfallId) {
        matchedCard = getCardById(card.scryfallId);
        if (matchedCard) matchType = 'exact';
      }

      // Priority 2: Try exact match by set + collector number
      if (!matchedCard && card.setCode && card.collectorNumber) {
        matchedCard = getCardBySetNumber(card.setCode, card.collectorNumber);
        if (matchedCard) matchType = 'exact';
      }

      // Priority 3: Fallback to exact name match
      if (!matchedCard) {
        matchedCard = getCardByName(card.name);
        if (matchedCard) matchType = 'name';
      }

      // Priority 4: Fuzzy prefix search as last resort
      if (!matchedCard) {
        const prefixMatches = searchCardsByPrefix(card.name, 1);
        if (prefixMatches.length > 0) {
          matchedCard = prefixMatches[0];
          matchType = 'fuzzy';
        }
      }

      return {
        matchedCard: matchedCard ?? null,
        matchType,
        matchError: matchedCard ? undefined : 'Card not found in database',
      };
    },
    [getCardById, getCardBySetNumber, getCardByName, searchCardsByPrefix],
  );

  /** Batch match: match an array of cards and return results with original card data preserved */
  const matchCards = useCallback(
    <T extends MatchableCard>(cards: T[]): (T & CardMatchResult)[] => {
      return cards.map((card) => ({
        ...card,
        ...matchCard(card),
      }));
    },
    [matchCard],
  );

  return {
    matchCard,
    matchCards,
    getCardById,
    getCardBySetNumber,
    getCardByName,
    searchCardsByPrefix,
  };
}
